import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users2, Save, Trophy, ChevronDown, ChevronRight, Sparkles,
  UserPlus, X, StickyNote,
} from "lucide-react";
import RosterPicker from "./RosterPicker";

const SKILLS = ["forehand", "backhand", "serve", "dink", "volley", "strategy"];

/**
 * RosterProgressCard — shown on group lessons (clinic/mixer/open_play).
 *
 * Two jobs, on the same page so the coach never has to open a student profile
 * mid-session:
 *   1. Manage who is in the lesson (add existing students / remove one).
 *   2. Per-player entry: ratings, coach notes, areas, milestone, and an AI
 *      insight generated from that player's own observation — saved in one pass
 *      with POST /progress/bulk.
 */
export default function RosterProgressCard({ lessonId, onSaved }) {
  const [roster, setRoster] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState({ max_students: 0, booked_count: 0, status: "" });
  const [loaded, setLoaded] = useState(false);
  const [sharedNote, setSharedNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState({});          // uid -> {ratings, areas, notes, milestone, insights}
  const [expanded, setExpanded] = useState({});    // uid -> bool
  const [insightBusy, setInsightBusy] = useState({});
  const [manageOpen, setManageOpen] = useState(false);
  const [addIds, setAddIds] = useState([]);
  const [rosterBusy, setRosterBusy] = useState(false);

  const mergeForms = (list) => {
    setForms((cur) => {
      const next = { ...cur };
      const keep = new Set(list.map((m) => m.user_id));
      Object.keys(next).forEach((k) => { if (!keep.has(k)) delete next[k]; });
      list.forEach((m) => {
        if (!next[m.user_id]) {
          next[m.user_id] = {
            ratings: { ...(m.last_ratings || {}) },
            areas: "",
            notes: "",
            milestone: "",
            insights: "",
          };
        }
      });
      return next;
    });
  };

  const loadRoster = async () => {
    const { data } = await api.get(`/lessons/${lessonId}/roster`);
    const list = data.roster || [];
    setRoster(list);
    setWaitlist(data.waitlist || []);
    setMeta({
      max_students: data.max_students || 1,
      booked_count: data.booked_count == null ? list.length : data.booked_count,
      status: data.status || "",
    });
    mergeForms(list);
    setLoaded(true);
  };

  useEffect(() => {
    if (!lessonId) return;
    loadRoster().catch(() => setLoaded(true));
    api.get("/students")
      .then(({ data }) => setStudents(Array.isArray(data) ? data : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const setRating = (uid, skill, val) => {
    setForms((cur) => ({
      ...cur,
      [uid]: { ...cur[uid], ratings: { ...(cur[uid]?.ratings || {}), [skill]: Number(val) } },
    }));
  };
  const setField = (uid, field, val) => {
    setForms((cur) => ({ ...cur, [uid]: { ...cur[uid], [field]: val } }));
  };
  const toggleOpen = (uid) => setExpanded((cur) => ({ ...cur, [uid]: !cur[uid] }));

  const rosterIds = new Set(roster.map((m) => m.user_id));
  const available = students.filter((s) => !rosterIds.has(s.user_id));

  const addStudents = async () => {
    if (!addIds.length) return;
    setRosterBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/roster`, { student_ids: addIds });
      const n = (data.added || []).length;
      toast.success(`Added ${n} student${n === 1 ? "" : "s"}`);
      setAddIds([]);
      setManageOpen(false);
      await loadRoster();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add students");
    }
    setRosterBusy(false);
  };

  const removeStudent = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from this lesson?`)) return;
    setRosterBusy(true);
    try {
      await api.delete(`/lessons/${lessonId}/roster/${uid}`);
      toast.success(`${name} removed`);
      await loadRoster();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove student");
    }
    setRosterBusy(false);
  };

  const generateInsight = async (uid) => {
    const obs = ((forms[uid]?.notes || "") + " " + sharedNote).trim();
    if (!obs) {
      toast.error("Write a note for this player first — the AI builds on it");
      return;
    }
    setInsightBusy((cur) => ({ ...cur, [uid]: true }));
    try {
      const { data } = await api.post("/ai/student-insight", {
        student_id: uid,
        lesson_id: lessonId,
        observations: obs,
      });
      setField(uid, "insights", data.insights || "");
      toast.success("Insight ready — review before saving");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI failed");
    }
    setInsightBusy((cur) => ({ ...cur, [uid]: false }));
  };

  const save = async () => {
    const entries = roster.map((m) => ({
      student_id: m.user_id,
      skill_ratings: forms[m.user_id]?.ratings || {},
      areas_to_improve: forms[m.user_id]?.areas || "",
      notes: forms[m.user_id]?.notes || "",
      milestone: forms[m.user_id]?.milestone || "",
      insights: forms[m.user_id]?.insights || "",
    }));
    setSaving(true);
    try {
      const { data } = await api.post("/progress/bulk", {
        lesson_id: lessonId,
        shared_note: sharedNote,
        entries,
      });
      toast.success(`Logged progress for ${data.created} student${data.created !== 1 ? "s" : ""}`);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (!loaded) return null;

  const full = meta.max_students > 0 && roster.length >= meta.max_students;

  return (
    <div className="card-surface p-8" data-testid="roster-progress-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-2">
            <Users2 size={12} /> Group Log
          </div>
          <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">
            {roster.length === 0 ? "Build the roster" : `Progress for all ${roster.length} attendees`}
          </h3>
          <p className="text-xs text-[#A3A3A3] mt-2 max-w-xl">
            Add students, then rate each player, write a note, and get an AI insight — all here.
            Each student gets a timestamped entry on their timeline.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${full ? "text-[#F59E0B] border-[#F59E0B]/40" : "text-[#A3A3A3] border-white/10"}`}
            data-testid="roster-capacity"
          >
            {roster.length}/{meta.max_students || "—"} booked
          </span>
          <button
            type="button"
            onClick={() => setManageOpen((v) => !v)}
            className="btn-secondary px-4 py-2.5 text-xs inline-flex items-center gap-2"
            data-testid="roster-manage-toggle"
          >
            <UserPlus size={14} /> Manage roster
          </button>
        </div>
      </div>

      {manageOpen && (
        <div className="mt-6 border border-[#38BDF8]/40 p-5" data-testid="roster-manage-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">
            Add existing students
          </div>
          {full ? (
            <p className="text-xs text-[#F59E0B]">
              Session is full ({roster.length}/{meta.max_students}). Raise the capacity or remove a student first.
            </p>
          ) : (
            <>
              <RosterPicker
                students={available}
                selectedIds={addIds}
                onChange={setAddIds}
                title="Students not yet booked"
              />
              <div className="flex items-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={addStudents}
                  disabled={rosterBusy || addIds.length === 0}
                  className="btn-primary px-5 py-2.5 text-xs"
                  data-testid="roster-add-submit"
                >
                  {rosterBusy ? "Adding…" : `Add ${addIds.length || ""} to lesson`}
                </button>
                <button
                  type="button"
                  onClick={() => { setManageOpen(false); setAddIds([]); }}
                  className="text-[11px] text-[#A3A3A3] hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {roster.length === 0 ? (
        <div className="mt-6 border border-dashed border-white/10 p-6 text-sm text-[#A3A3A3]" data-testid="roster-empty">
          No students booked yet. Use <strong className="text-white">Manage roster</strong> to add players from your student list.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {roster.map((m) => {
            const form = forms[m.user_id] || { ratings: {}, areas: "", notes: "", milestone: "", insights: "" };
            const isOpen = !!expanded[m.user_id];
            return (
              <div key={m.user_id} className="border border-white/10" data-testid={`roster-row-${m.user_id}`}>
                <div className="flex items-start justify-between gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleOpen(m.user_id)}
                    className="flex items-start gap-3 text-left flex-1 min-w-0"
                    data-testid={`roster-expand-${m.user_id}`}
                    aria-expanded={isOpen}
                  >
                    <span className="text-[#A3A3A3] mt-1 shrink-0">
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <span className="min-w-0">
                      <span className="font-display text-xl font-black uppercase tracking-tight block">{m.name}</span>
                      <span className="text-[11px] text-[#A3A3A3] block">
                        DUPR ~{m.skill_level || "—"}
                        {m.goals ? ` · goal: ${m.goals}` : ""}
                      </span>
                      {m.last_areas && (
                        <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3] block mt-1">
                          Prev focus: <span className="text-white/80 normal-case">{m.last_areas}</span>
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    {(form.notes || form.insights) && (
                      <span className="text-[10px] uppercase tracking-widest text-[#38BDF8] inline-flex items-center gap-1" title="Has a note">
                        <StickyNote size={12} /> note
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeStudent(m.user_id, m.name)}
                      disabled={rosterBusy}
                      className="text-[#A3A3A3] hover:text-red-400"
                      title="Remove from lesson"
                      data-testid={`roster-remove-${m.user_id}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-white/10 p-4">
                    <div className="grid md:grid-cols-6 grid-cols-2 gap-2">
                      {SKILLS.map((s) => (
                        <div key={s}>
                          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">{s}</label>
                          <select
                            value={form.ratings[s] || 0}
                            onChange={(e) => setRating(m.user_id, s, e.target.value)}
                            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-1.5 text-sm"
                            data-testid={`rating-${m.user_id}-${s}`}
                          >
                            <option className="bg-[#141414]" value={0}>—</option>
                            {[1, 2, 3, 4, 5].map((v) => <option key={v} className="bg-[#141414]" value={v}>{v}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                        Coach notes for {m.name}
                      </label>
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setField(m.user_id, "notes", e.target.value)}
                        placeholder="What you saw this player do today — the stroke, the moment, what to fix…"
                        className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-2"
                        data-testid={`notes-${m.user_id}`}
                      />
                    </div>

                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => generateInsight(m.user_id)}
                        disabled={!!insightBusy[m.user_id]}
                        className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2"
                        data-testid={`insight-btn-${m.user_id}`}
                      >
                        <Sparkles size={13} /> {insightBusy[m.user_id] ? "Thinking…" : "AI insight"}
                      </button>
                      <span className="text-[11px] text-[#A3A3A3]">Builds on the notes above. You can edit it before saving.</span>
                    </div>

                    {form.insights && (
                      <textarea
                        rows={4}
                        value={form.insights}
                        onChange={(e) => setField(m.user_id, "insights", e.target.value)}
                        className="w-full bg-transparent border border-[#38BDF8]/30 focus:border-[#38BDF8] outline-none p-3 text-sm mt-3"
                        data-testid={`insights-${m.user_id}`}
                      />
                    )}

                    <div className="mt-4 grid md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={form.areas}
                        onChange={(e) => setField(m.user_id, "areas", e.target.value)}
                        placeholder="Areas to work on (optional)"
                        className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-xs"
                        data-testid={`areas-${m.user_id}`}
                      />
                      <div className="flex items-center gap-2">
                        <Trophy size={12} className="text-[#A3A3A3]" />
                        <input
                          type="text"
                          value={form.milestone}
                          onChange={(e) => setField(m.user_id, "milestone", e.target.value)}
                          placeholder="Milestone unlocked? (optional)"
                          className="flex-1 bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-xs"
                          data-testid={`milestone-${m.user_id}`}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {waitlist.length > 0 && (
        <div className="mt-6 border-t border-white/10 pt-5" data-testid="roster-waitlist">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F59E0B] font-bold">
            Waitlist · {waitlist.length} waiting
          </div>
          <div className="mt-3 space-y-2">
            {waitlist.map((w, i) => (
              <div key={w.user_id} className="flex items-baseline justify-between text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-bold uppercase">{i + 1}. {w.name}</span>
                  <span className="text-[11px] text-[#A3A3A3]">DUPR ~{w.skill_level || "—"}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#A3A3A3] mt-2">
            When a booked student is removed or withdraws, the first student here is promoted automatically.
          </p>
        </div>
      )}

      <div className="mt-6 border-t border-white/10 pt-5">
        <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
          Session note — applies to everyone (used when a player has no note of their own)
        </label>
        <textarea
          rows={2}
          value={sharedNote}
          onChange={(e) => setSharedNote(e.target.value)}
          placeholder="e.g. Great energy across the group today — worked station 3rd-shots for 30 min…"
          className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-2"
          data-testid="roster-shared-note"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving || roster.length === 0}
          className="btn-primary mt-4 px-5 py-3 text-xs inline-flex items-center gap-2"
          data-testid="roster-save"
        >
          <Save size={14} /> {saving ? "Saving…" : `Log progress for ${roster.length}`}
        </button>
      </div>
    </div>
  );
}
